import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { createForgedVehicleSurface } from '../assets/vehicleSurfaceForge';
import type { InputController } from '../core/InputController';

export type PlayerTuning = {
  cruiseSpeed: number;
  trimSpeed: number;
};

export type PlayfieldBounds = {
  halfWidth: number;
  halfHeight: number;
};

/**
 * THE FALLBACK AIRFRAME'S PALETTE.
 *
 * This is what renders for the fraction of a second before the forged
 * Wraithwing GLB arrives, and on any machine where that load fails — so it has
 * to match the real aircraft rather than contradict it.
 *
 * It previously did contradict it, badly: oxide red, light composite grey, a
 * violet spine and green engines. Purple and green on a black stealth
 * airframe read as neon toy, which is the opposite of the intent. Everything
 * here is now the same mirror-black family as the loaded model, with heat as
 * the ONLY colour on the aircraft.
 */
const HULL_BLACK = '#07090b';
const PANEL_BLACK = '#0d1013';
const TRIM_GRAPHITE = '#2a2e33';
/**
 * Engine heat. A military afterburner is white at the throat grading to deep
 * orange — never green. This is the single warm accent on the whole airframe,
 * which is exactly why it has to be the right warm.
 */
const ENGINE_CORE = '#ffd9a8';
const ENGINE_RIM = '#ff5a12';

function createHullGeometry(): THREE.ExtrudeGeometry {
  const shape = new THREE.Shape();
  shape.moveTo(0, 1.6);
  shape.lineTo(0.28, 1.0);
  shape.lineTo(0.58, 0.56);
  shape.lineTo(0.35, 0.46);
  shape.lineTo(1.2, -0.82);
  shape.lineTo(1.2, -1.16);
  shape.lineTo(0.42, -1.05);
  shape.lineTo(0.3, -1.55);
  shape.lineTo(0, -1.34);
  shape.lineTo(-0.3, -1.55);
  shape.lineTo(-0.42, -1.05);
  shape.lineTo(-1.2, -1.16);
  shape.lineTo(-1.2, -0.82);
  shape.lineTo(-0.35, 0.46);
  shape.lineTo(-0.58, 0.56);
  shape.lineTo(-0.28, 1.0);
  shape.closePath();
  const geometry = new THREE.ExtrudeGeometry(shape, {
    depth: 0.18,
    bevelEnabled: true,
    bevelSegments: 1,
    bevelSize: 0.06,
    bevelThickness: 0.05,
    curveSegments: 1,
  });
  geometry.center();
  return geometry;
}

export class Player {
  readonly group = new THREE.Group();
  readonly velocity = new THREE.Vector2();
  /** Previous fixed-step position, for render interpolation. */
  private readonly prevPos = new THREE.Vector2();
  /** Authoritative simulation position. `group.position` is the drawn one. */
  private readonly simPos = new THREE.Vector2();
  readonly hitRadius = 0.22;

  private readonly visualRoot = new THREE.Group();
  private readonly fallbackRoot = new THREE.Group();
  private readonly forgedSurface = createForgedVehicleSurface();
  private readonly move = new THREE.Vector2();
  private readonly hullGeometry = createHullGeometry();
  private readonly hullMaterial = new THREE.MeshPhysicalMaterial({
    // Mirror black: near-black base, high metalness, low roughness. Almost all
    // visible colour then comes from the environment reflection, which is what
    // makes a low-observable coating read as menacing rather than as grey.
    color: HULL_BLACK,
    roughness: 0.18,
    metalness: 0.94,
    clearcoat: 0.6,
    clearcoatRoughness: 0.16,
    envMapIntensity: 1.15,
  });
  private readonly trimMaterial = new THREE.MeshStandardMaterial({
    color: TRIM_GRAPHITE,
    roughness: 0.3,
    metalness: 0.9,
    envMapIntensity: 1.05,
  });
  /**
   * The dorsal spine.
   *
   * Was a row of glowing violet octahedra — the "weird rectangle across the
   * front". It is now unlit armour plating in panel black, so the spine reads
   * as structure rather than as decoration. Nothing on this aircraft glows
   * except its engines.
   */
  private readonly latticeMaterial = new THREE.MeshStandardMaterial({
    color: PANEL_BLACK,
    roughness: 0.26,
    metalness: 0.92,
    envMapIntensity: 1.0,
  });
  private readonly engineMaterial = new THREE.MeshBasicMaterial({
    color: ENGINE_CORE,
    toneMapped: false,
  });
  /** Outer ring of the exhaust, cooler than the throat. */
  private readonly engineRimMaterial = new THREE.MeshBasicMaterial({
    color: ENGINE_RIM,
    toneMapped: false,
    transparent: true,
    opacity: 0.85,
  });
  private readonly ownedGeometries: THREE.BufferGeometry[] = [this.hullGeometry];
  private readonly ownedMaterials: THREE.Material[] = [
    this.hullMaterial,
    this.trimMaterial,
    this.latticeMaterial,
    this.engineRimMaterial,
    this.engineMaterial,
  ];
  private readonly facetMesh: THREE.InstancedMesh;
  private readonly hitboxDisc: THREE.Mesh;
  private readonly importedBurners: THREE.Object3D[] = [];
  private importedRoot: THREE.Object3D | null = null;
  private disposed = false;
  private engineTime = 0;

  lives = 3;
  bombs = 3;
  /**
   * Main weapon state, set by the drifting pod.
   * HAILSTORM = widening spread, strongest close in.
   * ARCLIGHT  = narrow lance, better at range and against armour.
   */
  weapon: 'HAILSTORM' | 'ARCLIGHT' = 'HAILSTORM';
  /** 0-indexed into the eight power tiers (bible 06 §2). */
  power = 2;
  /** Missile subweapon tier. 0 = none; earned from bomb pickups. */
  missiles = 0;
  /**
   * Absorbs one hit. The mid-run recovery the loop was missing — a one-hit
   * death game with no replenishment means a bad thirty seconds ends the run,
   * which is why the stage was unseeable past the first minute.
   */
  shield = 0;

  /**
   * Apply a pod. Same colour levels you up; a different colour swaps the
   * weapon and costs one tier — so hoovering every pod is not automatically
   * correct, which is what makes the drifting colour a real decision.
   */
  applyPod(color: 'HAILSTORM' | 'ARCLIGHT'): 'level' | 'swap' {
    if (color === this.weapon) {
      this.power = Math.min(7, this.power + 1);
      return 'level';
    }
    this.weapon = color;
    this.power = Math.max(0, this.power - 1);
    return 'swap';
  }
  invulnerability = 0;
  fireCooldown = 0;
  bank = 0;

  constructor() {
    this.group.name = 'TH01_THUNDERHEAD';
    this.group.position.set(0, -16, 1.5);
    this.visualRoot.name = 'TH01_VisualRoot';
    this.fallbackRoot.name = 'TH01_ProceduralFallback';
    this.visualRoot.add(this.fallbackRoot);
    this.group.add(this.visualRoot);
    const heroLight = new THREE.PointLight('#d9efff', 7.5, 13, 2);
    heroLight.name = 'TH01_HeroLight';
    heroLight.position.set(0, -0.2, 4);
    this.group.add(heroLight);

    const hull = new THREE.Mesh(this.hullGeometry, this.hullMaterial);
    hull.name = 'TH01_Hull';
    hull.castShadow = true;
    this.fallbackRoot.add(hull);

    const canopyGeometry = new THREE.SphereGeometry(0.28, 16, 10);
    this.ownedGeometries.push(canopyGeometry);
    const canopyMaterial = new THREE.MeshPhysicalMaterial({
      color: '#13212b',
      roughness: 0.08,
      metalness: 0.22,
      clearcoat: 1,
      clearcoatRoughness: 0.04,
    });
    this.ownedMaterials.push(canopyMaterial);
    const canopy = new THREE.Mesh(canopyGeometry, canopyMaterial);
    canopy.name = 'TH01_Canopy';
    canopy.scale.set(0.82, 1.52, 0.48);
    canopy.position.set(0, 0.46, 0.22);
    this.fallbackRoot.add(canopy);

    const finGeometry = new THREE.BoxGeometry(0.12, 0.52, 0.26);
    this.ownedGeometries.push(finGeometry);
    for (const side of [-1, 1]) {
      const fin = new THREE.Mesh(finGeometry, this.trimMaterial);
      fin.name = side < 0 ? 'TH01_Fin_L' : 'TH01_Fin_R';
      fin.position.set(side * 0.38, -0.92, 0.26);
      fin.rotation.z = side * -0.38;
      this.fallbackRoot.add(fin);
    }

    const facetGeometry = new THREE.OctahedronGeometry(0.075, 0);
    this.ownedGeometries.push(facetGeometry);
    this.facetMesh = new THREE.InstancedMesh(facetGeometry, this.latticeMaterial, 8);
    this.facetMesh.name = 'TH01_FacetSpine';
    const dummy = new THREE.Object3D();
    for (let index = 0; index < 8; index += 1) {
      dummy.position.set(0, -0.74 + index * 0.2, 0.29);
      dummy.scale.set(0.8, 1, 0.55);
      dummy.updateMatrix();
      this.facetMesh.setMatrixAt(index, dummy.matrix);
    }
    this.fallbackRoot.add(this.facetMesh);

    // Two-part exhaust: a white-hot throat inside a wider orange bloom. One
    // flat disc reads as a painted dot; the pair reads as heat, and heat is
    // the only colour allowed on this airframe.
    const engineGeometry = new THREE.CircleGeometry(0.1, 16);
    const engineRimGeometry = new THREE.CircleGeometry(0.19, 16);
    this.ownedGeometries.push(engineGeometry, engineRimGeometry);
    for (const side of [-1, 1]) {
      const rim = new THREE.Mesh(engineRimGeometry, this.engineRimMaterial);
      rim.name = side < 0 ? 'TH01_EngineRim_L' : 'TH01_EngineRim_R';
      rim.position.set(side * 0.34, -1.43, 0.19);
      this.fallbackRoot.add(rim);

      const engine = new THREE.Mesh(engineGeometry, this.engineMaterial);
      engine.name = side < 0 ? 'TH01_Engine_L' : 'TH01_Engine_R';
      engine.position.set(side * 0.34, -1.43, 0.2);
      this.fallbackRoot.add(engine);
    }

    const hitboxGeometry = new THREE.RingGeometry(0.16, this.hitRadius, 24);
    this.ownedGeometries.push(hitboxGeometry);
    const hitboxMaterial = new THREE.MeshBasicMaterial({
      color: '#ffffff',
      transparent: true,
      opacity: 0.92,
      depthWrite: false,
      toneMapped: false,
    });
    this.ownedMaterials.push(hitboxMaterial);
    this.hitboxDisc = new THREE.Mesh(hitboxGeometry, hitboxMaterial);
    this.hitboxDisc.position.set(0, 0.35, 0.5);
    this.hitboxDisc.visible = false;
    this.group.add(this.hitboxDisc);
    void this.loadWraithwing();
  }

  update(
    delta: number,
    input: InputController,
    tuning: PlayerTuning,
    bounds: PlayfieldBounds,
  ): void {
    input.readMovement(this.move);
    const focused = input.isFocusHeld();
    const speed = focused ? tuning.trimSpeed : tuning.cruiseSpeed;
    this.prevPos.copy(this.simPos);
    this.velocity.copy(this.move).multiplyScalar(speed);
    this.simPos.x += this.velocity.x * delta;
    this.simPos.y += this.velocity.y * delta;
    this.simPos.x = THREE.MathUtils.clamp(
      this.simPos.x,
      -bounds.halfWidth + 1.2,
      bounds.halfWidth - 1.2,
    );
    this.simPos.y = THREE.MathUtils.clamp(
      this.simPos.y,
      -bounds.halfHeight + 1.6,
      bounds.halfHeight - 1.6,
    );
    // Collision reads group.position, so keep it authoritative here; render()
    // will nudge it to the interpolated value just before drawing.
    this.group.position.x = this.simPos.x;
    this.group.position.y = this.simPos.y;
    this.bank = THREE.MathUtils.damp(this.bank, -this.move.x * 0.18, 13, delta);
    this.group.rotation.y = this.bank;
    this.group.rotation.x = THREE.MathUtils.damp(
      this.group.rotation.x,
      this.move.y * 0.075,
      10,
      delta,
    );
    this.engineTime += delta;
    const burnerPulse = 1 + Math.sin(this.engineTime * 28) * 0.08;
    this.importedBurners.forEach((burner) => burner.scale.setScalar(burnerPulse));
    this.hitboxDisc.visible = focused;
    this.invulnerability = Math.max(0, this.invulnerability - delta);
    this.fireCooldown = Math.max(0, this.fireCooldown - delta);
    const blink = this.invulnerability > 0 && Math.floor(this.invulnerability * 14) % 2 === 0;
    this.group.visible = !blink;
  }

  /**
   * Called once per RENDER frame with the loop's alpha (0..1 between fixed
   * steps). Smooths the drawn position so a 120 Hz display shows motion at
   * 120 Hz even though the simulation ticks at 60.
   */
  /**
   * Place the ship for drawing.
   *
   * DRAWN EXACTLY AT THE SIMULATED POSITION. Two earlier attempts both made
   * the controls feel worse, in opposite ways:
   *
   *   1. Interpolating prevPos -> simPos draws the ship up to one full step
   *      (16.6 ms) BEHIND where it actually is. That is real input latency.
   *   2. Extrapolating past simPos removed that latency but overshot: at
   *      alpha 1 the ship sat 0.85 of a step ahead of itself and snapped back
   *      at the start of the next step. On a high-refresh display that is
   *      per-frame jitter, which reads as floaty and imprecise.
   *
   * Drawing at simPos has neither problem. The only cost is that motion is
   * quantised to the 60 Hz simulation instead of the display's refresh, and
   * for a ship travelling in a straight line that is imperceptible — whereas
   * both latency and jitter are immediately felt.
   */
  interpolate(_alpha: number): void {
    this.group.position.x = this.simPos.x;
    this.group.position.y = this.simPos.y;
  }

  reset(): void {
    this.group.position.set(0, -16, 1.5);
    this.group.rotation.set(0, 0, 0);
    this.velocity.set(0, 0);
    this.lives = 3;
    this.bombs = 3;
    this.power = 2;
    this.missiles = 0;
    this.shield = 0;
    this.weapon = 'HAILSTORM';
    this.invulnerability = 0;
    this.fireCooldown = 0;
    this.group.visible = true;
  }

  takeHit(): boolean {
    if (this.invulnerability > 0) return false;
    this.lives -= 1;
    this.invulnerability = 1.8;
    this.group.position.set(0, -16, 0.3);
    return true;
  }

  dispose(): void {
    this.disposed = true;
    this.ownedGeometries.forEach((geometry) => geometry.dispose());
    this.ownedMaterials.forEach((material) => material.dispose());
    this.disposeImportedModel();
    this.forgedSurface.dispose();
  }

  private async loadWraithwing(): Promise<void> {
    try {
      const gltf = await new GLTFLoader().loadAsync(
        '/models/thunderhead/wraithwing-xf7.glb',
      );
      const model = gltf.scene;
      model.name = 'TH01_WraithwingXF7_Imported';
      model.rotation.x = Math.PI / 2;
      model.scale.setScalar(0.76);
      model.updateMatrixWorld(true);

      const bounds = new THREE.Box3().setFromObject(model);
      const center = bounds.getCenter(new THREE.Vector3());
      model.position.sub(center);
      model.position.z += 0.08;
      model.traverse((child) => {
        if (!(child instanceof THREE.Mesh)) return;
        child.castShadow = true;
        child.receiveShadow = false;
        const materials = Array.isArray(child.material) ? child.material : [child.material];
        materials.forEach((material) => {
          if (!(material instanceof THREE.MeshStandardMaterial)) return;
          material.bumpMap = this.forgedSurface.bump;
          material.roughnessMap = this.forgedSurface.roughness;
          material.bumpScale = material.name === 'StealthHull' ? 0.032 : 0.02;
          // MIRROR BLACK. A stealth airframe is not matte — modern low-observable
          // coatings are dark and specular, so the aircraft reads as a black
          // mirror that catches the sky rather than as a grey toy.
          //
          // The recipe: near-black base, very high metalness, low roughness,
          // and a moderate environment reflection. High metalness with a dark
          // base means almost all visible colour comes from the reflection,
          // which is exactly what "mirror black" means physically.
          material.envMapIntensity = material.name === 'CockpitGlass' ? 1.6 : 1.15;
          material.bumpScale = 0.012;
          if (material.name === 'FighterTrim') {
            // No decorative trim. A thin dark graphite line, nothing more.
            material.color.set('#2a2e33');
            material.emissive.set('#000000');
            material.emissiveIntensity = 0;
            material.metalness = 0.9;
            material.roughness = 0.3;
          } else if (material.name === 'StealthHull') {
            material.color.set('#07090b');
            material.roughness = 0.16;
            material.metalness = 0.96;
          } else if (material.name === 'StealthPanel') {
            // One shade off the hull so panel breaks stay legible in reflection.
            material.color.set('#0d1013');
            material.roughness = 0.24;
            material.metalness = 0.92;
          } else if (material.name === 'Afterburner') {
            // The forge authors this electric cyan (#24dcff), and nothing here
            // overrode it — so the loaded aircraft kept a neon-blue exhaust
            // under an otherwise mirror-black airframe. A military afterburner
            // is white at the throat grading to orange; heat is the ONLY
            // colour permitted on this aircraft.
            material.color.set('#ffb066');
            material.emissive.set('#ff5a12');
            material.emissiveIntensity = 2.4;
            material.roughness = 0.35;
            material.metalness = 0.1;
          } else if (material.name === 'Weapon') {
            material.color.set('#15181b');
            material.roughness = 0.2;
            material.metalness = 0.95;
          } else if (material.name === 'Canopy') {
            // Smoked glass over gold film — the one non-black surface, and the
            // only place the eye is allowed to land.
            material.color.set('#141208');
            material.roughness = 0.04;
            material.metalness = 0.95;
          } else {
            // Anything unnamed inherits the mirror-black treatment rather than
            // keeping an authored colour that would break the read.
            material.color.set('#0a0d10');
            material.roughness = 0.2;
            material.metalness = 0.94;
          }
          material.needsUpdate = true;
        });
      });

      this.importedBurners.push(
        ...[
          model.getObjectByName('AfterburnerCore_L'),
          model.getObjectByName('AfterburnerCore_R'),
        ].filter((node): node is THREE.Object3D => Boolean(node)),
      );
      if (this.disposed) {
        model.traverse((child) => {
          if (!(child instanceof THREE.Mesh)) return;
          child.geometry.dispose();
          const materials = Array.isArray(child.material) ? child.material : [child.material];
          materials.forEach((material) => material.dispose());
        });
        return;
      }
      this.importedRoot = model;
      this.visualRoot.add(model);
      this.fallbackRoot.visible = false;
    } catch (error) {
      console.warn('Wraithwing GLB unavailable; retaining procedural TH-01 fallback.', error);
    }
  }

  private disposeImportedModel(): void {
    if (!this.importedRoot) return;
    const textures = new Set<THREE.Texture>();
    this.importedRoot.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;
      child.geometry.dispose();
      const materials = Array.isArray(child.material) ? child.material : [child.material];
      materials.forEach((material) => {
        Object.values(material).forEach((value) => {
          if (value instanceof THREE.Texture) textures.add(value);
        });
        material.dispose();
      });
    });
    textures.forEach((texture) => texture.dispose());
    this.importedRoot.removeFromParent();
    this.importedRoot = null;
  }
}
